import { ref, onMounted } from 'vue';
import axios from 'axios';
import { f7 } from 'framework7-vue';
import { Device } from '@capacitor/device';
import { WebAuthn } from '@darkedges/capacitor-native-webauthn';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import i18n from '@/js/i18n.js';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '../pinia-stores/auth.js';
import { useAccountStore } from '../pinia-stores/account.js';
import { useApimoneyStore } from '../pinia-stores/apimoney.js';
import { useKeycloakStore } from '../pinia-stores/keycloak.js';
import { useTransactAuthNumpad, PIN_MAX_ATTEMPTS, PIN_LOCKOUT_MS } from './useTransactAuthNumpad.js';
import { useAuthMethodPicker } from './useAuthMethodPicker.js';

const path = import.meta.env.VITE_APP_URL;

const BIOMETRIC_SERVER = 'www.avocash.com';
const AUTH_INTENT_KEY = '__avocash_pending_auth_intent';
const AUTH_INTENT_TTL_MS = 5 * 60 * 1000;
const BIOMETRIC_MAX_ATTEMPTS = 3;

const noop = () => {};

function waitFor(predicate, timeoutMs = 5000, intervalMs = 200) {
  return new Promise((resolve) => {
    if (predicate()) { resolve(true); return; }
    const deadline = Date.now() + timeoutMs;
    const timer = setInterval(() => {
      if (predicate() || Date.now() >= deadline) {
        clearInterval(timer);
        resolve(predicate());
      }
    }, intervalMs);
  });
}

const BIOMETRIC_ERROR = {
  BIOMETRICS_UNAVAILABLE: 1,
  USER_LOCKOUT: 2,
  BIOMETRICS_NOT_ENROLLED: 3,
  USER_TEMPORARY_LOCKOUT: 4,
  AUTHENTICATION_FAILED: 10,
  APP_CANCEL: 11,
  INVALID_CONTEXT: 12,
  NOT_INTERACTIVE: 13,
  PASSCODE_NOT_SET: 14,
  SYSTEM_CANCEL: 15,
  USER_CANCEL: 16,
  USER_FALLBACK: 17,
};

const USER_INITIATED_STOP = new Set([
  BIOMETRIC_ERROR.APP_CANCEL,
  BIOMETRIC_ERROR.INVALID_CONTEXT,
  BIOMETRIC_ERROR.NOT_INTERACTIVE,
  BIOMETRIC_ERROR.SYSTEM_CANCEL,
  BIOMETRIC_ERROR.USER_CANCEL,
  BIOMETRIC_ERROR.USER_FALLBACK,
]);

function biometricErrorCode(error) {
  const raw = Number(error?.code);
  return Number.isFinite(raw) ? raw : null;
}

function isUserInitiatedBiometricStop(error) {
  const code = biometricErrorCode(error);
  return code != null && USER_INITIATED_STOP.has(code);
}

function isPasskeyUserStop(error) {
  const name = error?.name;
  return name === 'NotAllowedError' || name === 'AbortError';
}

// Build a human-readable reason for the failure dialog so the user (and we)
// can finally see WHY a passkey flow failed: HTTP status + server message for
// backend rejections, WebAuthnError code/message for browser-side errors.
function passkeyErrorDetail(error) {
  const parts = [];
  const status = error?.response?.status;
  if (status) parts.push(`HTTP ${status}`);
  const serverMessage = error?.response?.data?.message || error?.response?.data?.error;
  if (typeof serverMessage === 'string' && serverMessage.length > 0) parts.push(serverMessage);
  if (error?.code) parts.push(error.code);
  if (!serverMessage && error?.message) parts.push(error.message);
  return parts.join(' — ');
}

// The backend returns `{ publicKey: { ... }, requestId }` from the yubico
// toCredentials(Create|Get)Json serialization. The web plugin delegates
// directly to @simplewebauthn/browser, which expects the PublicKeyCredential
// options unwrapped (no `publicKey:` envelope, no `requestId`); the native
// plugin accepts the same unwrapped shape.
function normalizeWebAuthnOptions(request) {
  if (!request || typeof request !== 'object') return {};
  const options = request.publicKey && typeof request.publicKey === 'object'
    ? { ...request.publicKey }
    : { ...request };
  delete options.requestId;
  return options;
}

function saveAuthIntent(intent) {
  try {
    window.__piniaLcStorage?.set(AUTH_INTENT_KEY, { ...intent, expiresAt: Date.now() + AUTH_INTENT_TTL_MS });
  } catch (error) {
    console.error('Failed to save auth intent', error);
  }
}

function takeAuthIntent() {
  try {
    const intent = window.__piniaLcStorage?.get(AUTH_INTENT_KEY);
    if (!intent) return null;
    window.__piniaLcStorage?.remove(AUTH_INTENT_KEY);
    if (!intent.expiresAt || intent.expiresAt <= Date.now()) return null;
    return intent;
  } catch (error) {
    return null;
  }
}

function biometryTypeLabel(type) {
  switch (type) {
    case 1: return i18n.global.t('auth.touchID');
    case 2: return i18n.global.t('auth.faceID');
    case 3: return i18n.global.t('auth.fingerprint');
    case 4: return i18n.global.t('auth.faceAuthentication');
    case 5: return i18n.global.t('auth.iris');
    case 6: return i18n.global.t('auth.multipleBiometrics');
    case 7: return i18n.global.t('auth.deviceCredential');
    default: return i18n.global.t('auth.biometric');
  }
}

export function useTransactAuth(options = {}) {
  const {
    f7route, f7router,
    baseTransfer: baseTransferProp,
    userPaymentProvider: userPaymentProviderProp,
    paymentPageSuccessCallback: paymentPageSuccessCallbackFn,
    stripeValidate: stripeValidateFn,
    openLoginOtherOptions: openLoginOtherOptionsFn,
    toastIcon: toastIconProp,
    payment_detail: paymentDetailProp,
  } = options;
  const t = i18n.global.t;

  const authStore = useAuthStore();
  const accountStore = useAccountStore();
  const apimoneyStore = useApimoneyStore();
  const keycloakStore = useKeycloakStore();

  const { profile } = storeToRefs(authStore);
  const { userAccount } = storeToRefs(accountStore);


  const openloginScreen = ref('');
  const biometricIsAvailable = ref(false);
  const biometryType = ref(null);
  const current_page = ref('');
  const isWebAuthnAvailable = ref(false);
  const credName = ref('');
  const deviceId = ref('');
  const authDataRestored = ref(false);
  const baseTransfer = ref(baseTransferProp || null);
  const userPaymentProvider = ref(userPaymentProviderProp || null);
  const payment_detail = ref(paymentDetailProp || null);
  const toastIcon = ref(toastIconProp || null);

  async function restoreAuthData() {
    const paymentLinkId = f7.views.main.router.currentRoute.params.paymentLinkId;
    const transferDetail = paymentLinkId
      ? window.__piniaLcStorage.get(paymentLinkId)
      : null;
    const savedByIdempotency =
      baseTransfer.value?.idempotency_key &&
      window.__piniaLcStorage.get(baseTransfer.value.idempotency_key);
    const authData = transferDetail || savedByIdempotency;
    if (!authData) return;
    let paymentCriteria = null;
    try {
      if (transferDetail) {
        baseTransfer.value = transferDetail.baseTransfer;
        payment_detail.value = transferDetail.payment_detail;
        userPaymentProvider.value = transferDetail.userPaymentProvider;
        apimoneyStore.setBaseTransfer(transferDetail.baseTransfer);
        apimoneyStore.setUserPaymentProvider(transferDetail.userPaymentProvider);
        paymentCriteria = {
          currencyCode: transferDetail.baseTransfer.currency,
          countryCode: transferDetail.baseTransfer.countryCode,
        };
      } else if (savedByIdempotency) {
        baseTransfer.value = savedByIdempotency.base_Transfer;
        userPaymentProvider.value = savedByIdempotency.user_payment_provider;
        apimoneyStore.setBaseTransfer(savedByIdempotency.base_Transfer);
        apimoneyStore.setUserPaymentProvider(savedByIdempotency.user_payment_provider);
        paymentCriteria = { currencyCode: baseTransfer.value.currency, countryCode: baseTransfer.value.countryCode };
      }
      await apimoneyStore.getPaymentTransferMethods(paymentCriteria);
      authDataRestored.value = true;
      if (authDataRestored.value && keycloakStore.tokenParsed?.acr === 'gold') {
        await doPaymentPageSuccessCallback(baseTransfer.value, userPaymentProvider.value);
      }
    } catch (error) {
      console.error('Error restoring auth data:', error);
    } finally {
      if (paymentLinkId) window.__piniaLcStorage.remove(paymentLinkId);
      if (baseTransfer.value?.idempotency_key) window.__piniaLcStorage.remove(baseTransfer.value.idempotency_key);
    }
  }

  async function restoreAuthIntent() {
    const intent = takeAuthIntent();
    if (!intent) return;
    if (keycloakStore.tokenParsed?.acr !== 'gold') {
      saveAuthIntent(intent);
      return;
    }
    if (intent.type === 'navigate' && intent.route) {
      f7router?.navigate(intent.route);
    } else if (intent.type === 'payment') {
      await doPaymentPageSuccessCallback(intent.payload?.baseTransfer, intent.payload?.userPaymentProvider);
    } else {
      await defaultAuthSuccess();
    }
  }

  async function checkWebAuthnAvailability() {
    if (Capacitor.isNativePlatform()) {
      try {
        const { value } = await WebAuthn.isWebAuthnAvailable();
        isWebAuthnAvailable.value = !!value;
      } catch (error) {
        isWebAuthnAvailable.value = false;
      }
      return;
    }
    if (!window.PublicKeyCredential) {
      isWebAuthnAvailable.value = false;
      return;
    }
    // Do not gate the picker on a platform authenticator being available:
    // "Use another device" (hybrid/roaming passkeys) works on web even when
    // the local device has no Windows Hello / security key, so any browser
    // that supports WebAuthn is eligible to run a ceremony.
    isWebAuthnAvailable.value = true;
  }

  async function checkBiometry() {
    if (!Capacitor.isNativePlatform()) {
      biometricIsAvailable.value = false;
      return;
    }
    try {
      const result = await NativeBiometric.isAvailable();
      biometricIsAvailable.value = !!(result.isAvailable && result.strongBiometryIsAvailable);
      biometryType.value = result.biometryType ?? null;
    } catch (error) {
      console.error('Biometry check failed', error);
      biometricIsAvailable.value = false;
    }
  }

  async function defaultAuthSuccess() {
    if (current_page.value === 'paymentPage') {
      await doPaymentPageSuccessCallback();
    } else if (current_page.value === 'tamtam_main_page') {
      f7router.navigate({ name: 'home' });
    }
  }

  async function resolveAuthSuccess(onSuccess, method) {
    if (typeof onSuccess === 'function') {
      await onSuccess({ method });
      return;
    }
    await defaultAuthSuccess();
  }

  function selectBiometricOrPIN(options = {}) {
    if (biometricIsAvailable.value) {
      loginBiometricAuth(options);
    } else {
      openAuthloginScreen(options);
    }
  }

  async function loginBiometricAuth(options = {}) {
    const {
      onSuccess = null,
      onCancel = null,
      reason = t('common.biometricSignOnDescription'),
      title = t('common.biometricSignOnTitle'),
    } = options;

    if (!Capacitor.isNativePlatform()) {
      return 'failed';
    }

    try {
      const result = await NativeBiometric.isAvailable();
      if (!result.isAvailable || !result.strongBiometryIsAvailable) {
        biometricIsAvailable.value = false;
        return 'failed';
      }
      biometricIsAvailable.value = true;
      biometryType.value = result.biometryType ?? null;

      try {
        await NativeBiometric.setCredentials({
          username: keycloakStore.subject,
          password: profile.value?.PIN || '',
          server: BIOMETRIC_SERVER,
        });
      } catch (error) {
        console.error('Failed to save biometric credentials', error);
      }

      const promptDescription = t('common.biometricPromptDescription', { biometry: biometryTypeLabel(result.biometryType) });

      await NativeBiometric.verifyIdentity({
        reason: reason || promptDescription,
        title,
        description: promptDescription,
        negativeButtonText: t('common.cancel'),
        useFallback: true,
        fallbackTitle: t('common.fallback'),
        maxAttempts: BIOMETRIC_MAX_ATTEMPTS,
      });

      await resolveAuthSuccess(onSuccess, 'biometric');
      return 'success';
    } catch (error) {
      if (isUserInitiatedBiometricStop(error)) {
        if (typeof onCancel === 'function') {
          onCancel(error);
        } else {
          doOpenLoginOtherOptions(options);
        }
        return 'cancelled';
      }
      console.error('Biometric authentication failed', error);
      return 'failed';
    }
  }

  async function savedBiometricAuth() {
    try {
      await NativeBiometric.setCredentials({
        username: keycloakStore.subject,
        password: profile.value?.PIN || '',
        server: BIOMETRIC_SERVER,
      });
    } catch (error) {
      console.error('Failed to save biometric credentials', error);
    }
  }

  async function deleteBiometricAuth() {
    try {
      await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
    } catch (error) {
      console.error('Error deleting biometric credentials:', error);
    }
  }

  async function checkAuth(options = {}) {
    const {
      onSuccess = null,
      onCancel = null,
      onFailure = null,
      intent = null,
      reason = '',
      timeoutMs = 120000,
      methods = ['biometric', 'passkey', 'pin', 'stepup'],
    } = options;

    const flowOptions = { onSuccess, onCancel, onFailure, intent, reason, timeoutMs, methods };
    await Promise.all([checkWebAuthnAvailability(), checkBiometry()]);

    const successFn = onSuccess || defaultAuthSuccess;

    // Best practice: a session that already completed step-up (gold ACR) must
    // not be prompted again. Short-circuit so the action resumes immediately.
    if (methods.includes('stepup') && keycloakStore.tokenParsed?.acr === 'gold') {
      await resolveAuthSuccess(successFn, 'stepup');
      return true;
    }

    // Web/PWA: biometrics are native-only, and auto-firing the browser passkey
    // prompt (or silently redirecting to Keycloak) mid-flow is jarring. Present
    // the method picker with the options this session can use so the user
    // consciously chooses how to authenticate. "Use your password" falls back
    // to the server-side step-up round trip.
    if (!Capacitor.isNativePlatform() && methods.includes('stepup')) {
      const webMethods = [
        ...(methods.includes('passkey') && isWebAuthnAvailable.value ? ['passkey'] : []),
        ...(methods.includes('pin') && profile.value?.PIN ? ['pin'] : []),
        'password',
      ];
      if (webMethods.length === 1) {
        const stepUpResult = await keycloakAuthStepUpLogin({ ...flowOptions, onSuccess: successFn });
        return stepUpResult === 'success' || stepUpResult === 'redirect';
      }
      return doOpenLoginOtherOptions({
        ...flowOptions,
        onSuccess: successFn,
        methods: webMethods,
      });
    }

    // Deterministic cascade: each factor either succeeds, is cancelled by the
    // user (stop the flow), or fails for a technical reason (escalate).
    // Biometrics first, then passkeys, then the local PIN numpad, then
    // server-side step-up as the final (least convenient) fallback.
    if (methods.includes('biometric') && biometricIsAvailable.value) {
      const biometricResult = await loginBiometricAuth({ ...flowOptions, onSuccess: successFn, reason });
      if (biometricResult === 'success') return true;
      if (biometricResult === 'cancelled') return false;
    }

    if (methods.includes('passkey') && isWebAuthnAvailable.value) {
      const passkeyResult = await verifyPasskey({ ...flowOptions, onSuccess: successFn });
      if (passkeyResult === 'success') return true;
      if (passkeyResult === 'cancelled') return false;
    }

    if (methods.includes('pin') && profile.value?.PIN) {
      const pinResult = await openAuthloginScreen({ ...flowOptions, onSuccess: successFn });
      if (pinResult === 'success') return true;
      if (pinResult === 'cancelled' || pinResult === 'failed') return false;
    }

    if (methods.includes('stepup')) {
      const stepUpResult = await keycloakAuthStepUpLogin({ ...flowOptions, onSuccess: successFn });
      // 'redirect' means a Keycloak round-trip is in flight; the action is
      // resumed by restoreAuthIntent / restoreAuthData once the token is gold.
      if (stepUpResult === 'success' || stepUpResult === 'redirect') return true;
    }

    if (typeof onFailure === 'function') {
      await onFailure(new Error('No authentication method available'));
    }
    return false;
  }

  async function keycloakAuthStepUpLogin(options = {}) {
    const { onSuccess = null, intent = null } = options;

    const userAcr = keycloakStore.tokenParsed?.acr;
    if (userAcr === 'gold') {
      await resolveAuthSuccess(onSuccess, 'stepup');
      return 'success';
    }

    const paymentLinkId = f7.views.main.router.currentRoute.params.paymentLinkId;
    const transfer_detail = {
      baseTransfer: baseTransfer.value,
      payment_detail: payment_detail.value || null,
      userPaymentProvider: userPaymentProvider.value,
      time_stamp: new Date().getTime() + AUTH_INTENT_TTL_MS,
    };
    if (paymentLinkId && baseTransfer.value && userPaymentProvider.value) {
      window.__piniaLcStorage.set(paymentLinkId, transfer_detail);
    }
    if (authDataRestored.value && baseTransfer.value?.idempotency_key) {
      window.__piniaLcStorage.remove(baseTransfer.value.idempotency_key);
    }
    if (baseTransfer.value?.idempotency_key && userPaymentProvider.value) {
      const dataToSave = { base_Transfer: baseTransfer.value, user_payment_provider: userPaymentProvider.value };
      window.__piniaLcStorage.set(baseTransfer.value.idempotency_key, dataToSave);
    }
    if (intent) {
      saveAuthIntent(intent);
    }

    const curpath = f7.views.main.router.currentRoute.url;
    const loginOptions = {
      acr: { values: ['gold'], essential: true },
      locale: navigator.language.split('-')[0],
    };
    if (Capacitor.getPlatform() !== 'web') {
      loginOptions.redirectUri = `${import.meta.env.VITE_AVOCASH_MOBILE_RD_URL_SCHEME || 'avocash://login'}${curpath}`;
    } else {
      // Web/PWA: the silent check-sso round trip can return an unverified
      // session (non-gold ACR) without ever prompting, which stranded the app
      // on the home page. force a real login so the step-up actually happens.
      loginOptions.prompt = 'login';
    }

    await keycloakStore.login(loginOptions);
    return 'redirect';
  }

  async function createPasskey(options = {}) {
    const {
      onCancel = null,
    } = options;
    try {
      const { data: request } = await axios({
        url: `${path}webauthn/register`,
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloakStore.token}` },
      });
      const requestId = request.requestId;
      const registrationOptions = normalizeWebAuthnOptions(request);
      const publicKeyCredential = await WebAuthn.startRegistration(registrationOptions);
      const response = await axios({
        url: `${path}webauthn/register/finish`,
        method: 'POST',
        params: { credName: credName.value, credential: JSON.stringify(publicKeyCredential), requestId, deviceId: deviceId.value },
        headers: { Authorization: `Bearer ${keycloakStore.token}` },
      });
      if (response.data !== true) {
        f7.dialog.alert(t('auth.passkeyEnrollFailedMessage'), t('auth.passkeyEnrollFailedTitle'));
        return 'failed';
      }
      await accountStore.loadAccount(keycloakStore.subject);
      await waitFor(() => Boolean(userAccount.value?.handle));
      if (userAccount.value && userAccount.value.handle.length > 0) {
        return verifyPasskey(options);
      }
      f7.dialog.alert(t('auth.passkeyEnrollPendingMessage'), t('auth.passkeyEnrollFailedTitle'));
      return 'failed';
    } catch (error) {
      console.error('createPasskey error', error);
      if (isPasskeyUserStop(error)) {
        if (typeof onCancel === 'function') {
          onCancel(error);
        }
        return 'cancelled';
      }
      const detail = passkeyErrorDetail(error);
      f7.dialog.alert(
        detail ? `${t('auth.passkeyEnrollFailedMessage')}\n\n${detail}` : t('auth.passkeyEnrollFailedMessage'),
        t('auth.passkeyEnrollFailedTitle')
      );
      return 'failed';
    }
  }

  async function verifyPasskey(options = {}) {
    const {
      onSuccess = null,
      onCancel = null,
      timeoutMs = 120000,
    } = options;

    if (!isWebAuthnAvailable.value) return 'failed';
    // The capacity to run a passkey ceremony may not exist on this platform
    // (e.g. plugin without a web implementation). Fail fast instead of
    // throwing a confusing error in the console.
    if (typeof WebAuthn?.startAuthentication !== 'function') return 'failed';

    try {
      const { data: request } = await axios({
        url: `${path}webauthn/login`,
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloakStore.token}` },
      });
      // Backend reports no passkey is registered for this account: propose
      // enrollment instead of running a (guaranteed to fail) ceremony.
      if (request?.needsRegistration === true) {
        const enrolled = await confirmPasskeyEnrollment(options);
        if (enrolled === 'success') return 'success';
        return 'cancelled';
      }
      const requestId = request.requestId;
      const credentialOptions = normalizeWebAuthnOptions(request);
      const requestOptions = {
        ...credentialOptions,
        timeout: Number(credentialOptions.timeout) > 0 ? credentialOptions.timeout : timeoutMs,
        userVerification: 'required',
        hints: f7.device.desktop ? ['hybrid', 'client-device'] : ['client-device'],
      };
      const publicKeyCredential = await WebAuthn.startAuthentication(requestOptions);
      const { data: loginResponse } = await axios({
        url: `${path}webauthn/login/finish`,
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloakStore.token}` },
        params: { credential: JSON.stringify(publicKeyCredential), requestId },
      });
      if (loginResponse === true) {
        await resolveAuthSuccess(onSuccess, 'passkey');
        return 'success';
      }
      // Unexpected success payload: show it rather than silently dropping
      // the passkey option (which would collapse into a Keycloak redirect).
      f7.dialog.alert(
        `${t('auth.passkeyFailureMessage')}\n\n${JSON.stringify(loginResponse)}`,
        t('auth.passkeyFailureTitle')
      );
      return 'cancelled';
    } catch (error) {
      console.error('verifyPasskey error', error);
      if (isPasskeyUserStop(error)) {
        if (typeof onCancel === 'function') {
          onCancel(error);
        } else {
          doOpenLoginOtherOptions(options);
        }
        return 'cancelled';
      }
      // The account has no passkey registered yet (HTTP 409 from
      // startLogin when the user handle is blank). Offering the choices up
      // front prevents the "Use another device" flow from collapsing into a
      // silent Keycloak redirect with an invisible console error.
      if (error?.response?.status === 409) {
        const enrolled = await confirmPasskeyEnrollment(options);
        if (enrolled === 'success') return 'success';
        return 'cancelled';
      }
      // Any other genuine failure: surface the real reason instead of
      // quietly falling through to the password/Keycloak path, and reopen the
      // picker so the user stays in control of the next step.
      const detail = passkeyErrorDetail(error);
      f7.dialog.alert(
        detail ? `${t('auth.passkeyFailureMessage')}\n\n${detail}` : t('auth.passkeyFailureMessage'),
        t('auth.passkeyFailureTitle')
      );
      return 'cancelled';
    }
  }

  function confirmPasskeyEnrollment(options) {
    return new Promise((resolve) => {
      f7.dialog.confirm(
        t('auth.passkeyEnrollPrompt'),
        t('auth.passkeyEnrollTitle'),
        () => {
          createPasskey(options)
            .then((result) => resolve(result === 'success' ? 'success' : 'cancelled'))
            .catch(() => resolve('cancelled'));
        },
        () => resolve('cancelled')
      );
    });
  }

  function doOpenLoginOtherOptions(options = {}) {
    if (openLoginOtherOptionsFn) {
      openLoginOtherOptionsFn(options);
      return Promise.resolve(false);
    }

    // `options.methods` (when provided) restricts what the picker lists; the
    // password entry always stays available as the server-side step-up
    // fallback. Otherwise surface every method this session can actually use.
    const baseMethods = Array.isArray(options.methods)
      ? [...new Set([...options.methods, 'password'])]
      : [
          ...(biometricIsAvailable.value ? ['biometric'] : []),
          ...(isWebAuthnAvailable.value ? ['passkey'] : []),
          ...(profile.value?.PIN ? ['pin'] : []),
          'password',
        ];

    // Methods that already failed for a technical reason (e.g. the backend
    // refuses a webauthn/login challenge) are dropped so the retry loop does
    // not keep re-opening the picker with a broken option. User-initiated
    // cancels keep the full list so the user can pick again.
    const failedMethods = Array.isArray(options._failedMethods)
      ? options._failedMethods
      : [];
    const methods = baseMethods.filter((m) => !failedMethods.includes(m));

    // Nothing left but the server-side step-up; run it directly instead of
    // presenting a single-option chooser yet again.
    if (methods.length === 1 && methods[0] === 'password') {
      const stepUpResult = keycloakAuthStepUpLogin(options);
      return Promise.resolve(stepUpResult === 'success' || stepUpResult === 'redirect');
    }

    const picker = useAuthMethodPicker();
    return picker
      .open({
        reason: options.reason || '',
        methods,
        biometryLabel: biometryTypeLabel(biometryType.value),
      })
      .then(async (method) => {
        if (!method) return false;
        // The underlying methods get no-op onCancel/onFailure so they do NOT
        // reopen the picker themselves; the returned status drives the retry
        // loop here, keeping a single picker open at a time. A successful
        // attempt resolves through the original `onSuccess` (e.g. the profile
        // edit navigation) and reports `true` back to the caller.
        const attempt = async (run) => {
          const result = await run({ ...options, onCancel: noop, onFailure: noop });
          if (result === 'success') return true;
          if (result === 'cancelled') {
            return doOpenLoginOtherOptions(options);
          }
          return doOpenLoginOtherOptions({
            ...options,
            _failedMethods: [...failedMethods, method],
          });
        };

        if (method === 'biometric') return attempt((o) => loginBiometricAuth(o));
        if (method === 'passkey') return attempt((o) => verifyPasskey(o));
        if (method === 'pin') return attempt((o) => openAuthloginScreen(o));

        const stepUpResult = await keycloakAuthStepUpLogin(options);
        return stepUpResult === 'success' || stepUpResult === 'redirect';
      });
  }

  function openAuthloginScreen(options = {}) {
    const {
      onSuccess = null,
      onCancel = null,
      onFailure = null,
    } = options;

    if (!profile.value?.PIN) {
      // No local PIN configured for this account; delegate to the fallback flow.
      if (typeof onFailure === 'function') {
        onFailure(new Error('No PIN configured'));
      } else {
        doOpenLoginOtherOptions(options);
      }
      return Promise.resolve('failed');
    }

    const numpad = useTransactAuthNumpad();
    const subject = keycloakStore.subject || profile.value?.sub || 'anon';
    const verify = (enteredPin) => Boolean(profile.value?.PIN) && enteredPin === profile.value.PIN;

    const outcome = numpad.open({
      verify,
      subject,
      maxAttempts: PIN_MAX_ATTEMPTS,
      lockoutMs: PIN_LOCKOUT_MS,
    });

    outcome.then((result) => {
      if (result === 'success') {
        resolveAuthSuccess(onSuccess, 'pin');
      } else if (result === 'cancelled') {
        if (typeof onCancel === 'function') {
          onCancel(new Error('PIN authentication cancelled'));
        } else {
          doOpenLoginOtherOptions(options);
        }
      } else if (typeof onFailure === 'function') {
        onFailure(new Error('PIN authentication failed'));
      }
    });

    return outcome;
  }

  function authLoginScreenClose() {
    useTransactAuthNumpad().close();
  }

  function errorCallback() {
    keycloakAuthStepUpLogin();
  }

  async function doPaymentPageSuccessCallback(bt, upp) {
    if (paymentPageSuccessCallbackFn) {
      return paymentPageSuccessCallbackFn(bt, upp);
    }
    const transferData = bt || baseTransfer.value || apimoneyStore.baseTransfer;
    const paymentProvider = upp || userPaymentProvider.value;
    try {
      if (paymentProvider.paymentPartner === 'stripe') {
        if (transferData && transferData.transaction_type === 'CASH_IN') {
          if (stripeValidateFn) {
            await stripeValidateFn();
          }
        } else if (transferData && (transferData.transaction_type === 'CASH_OUT' || transferData.transaction_type === 'TRANSFER' || transferData.transaction_type === 'AIRTIME')) {
          await apimoneyStore.authorizeCashOutMobileMoney(transferData);
        }
      } else if (paymentProvider.paymentPartner === 'cinetpay') {
        if (transferData && transferData.transaction_type === 'CASH_IN' && ['MOBILE_MONEY', 'WALLET', 'CREDIT_CARD'].includes(paymentProvider.paymentMethod)) {
          await apimoneyStore.authorizeCashInMobileMoney(transferData);
        } else if (transferData && (transferData.transaction_type === 'CASH_OUT' || transferData.transaction_type === 'TRANSFER' || transferData.transaction_type === 'AIRTIME') && ['MOBILE_MONEY', 'WALLET', 'CREDIT_CARD', 'BANK_TRANSFER'].includes(paymentProvider.paymentMethod)) {
          await apimoneyStore.authorizeCashOutMobileMoney(transferData);
        }
      } else if (paymentProvider.paymentPartner === 'avocash') {
        if (transferData && transferData.transaction_type === 'CASH_IN') {
          await apimoneyStore.authorizeCashInMobileMoney(transferData);
        } else if (transferData && (transferData.transaction_type === 'CASH_OUT' || transferData.transaction_type === 'TRANSFER' || transferData.transaction_type === 'AIRTIME')) {
          await apimoneyStore.authorizeCashOutMobileMoney(transferData);
        }
      } else {
        console.error(`Unsupported payment provider: ${paymentProvider}`);
        f7.dialog.alert('The selected payment provider is not supported. Please try again with a valid provider.');
      }
    } catch (error) {
      console.error('Error in paymentPageSuccessCallback:', error);
    }
  }

  function validatedDialog() {
    if (!toastIcon.value) {
      toastIcon.value = f7.toast.create({
        icon: f7.device.ios
          ? '<i class="f7-icons text-color-green border-color-gray color-green" style="font-size:150px">checkmark_circle</i>'
          : '<i class="material-icons text-color-green border-color-gray color-green" style="font-size:150px">check_circle_outline</i>',
        text: '',
        cssClass: 'toast_validate',
        position: 'center',
        closeTimeout: 3000,
      });
    }
    toastIcon.value.open();
  }

  onMounted(async () => {
    current_page.value = f7.views.main.router.currentRoute.name;
    await Promise.all([checkWebAuthnAvailability(), checkBiometry()]);
    const [deviceInfo, deviceIdVal] = await Promise.all([Device.getInfo(), Device.getId()]);
    credName.value = [
      Capacitor.isNativePlatform()
        ? `${deviceIdVal.identifier} ${deviceInfo.name}`
        : `${deviceIdVal.identifier} ${deviceInfo.operatingSystem}`,
      deviceInfo.manufacturer,
      deviceInfo.model,
    ].filter(Boolean).join(' ');
    deviceId.value = deviceIdVal.identifier;
    if (keycloakStore.tokenParsed?.acr === 'gold') {
      await restoreAuthData();
      await restoreAuthIntent();
    }
  });

  return {
    openloginScreen, biometricIsAvailable, biometryType, current_page, isWebAuthnAvailable,
    credName, deviceId, authDataRestored, baseTransfer, userPaymentProvider,
    profile, userAccount,
    restoreAuthData, restoreAuthIntent, checkWebAuthnAvailability, checkBiometry, selectBiometricOrPIN,
    loginBiometricAuth, savedBiometricAuth, deleteBiometricAuth, checkAuth,
    keycloakAuthStepUpLogin, createPasskey, verifyPasskey,
    openLoginOtherOptions: doOpenLoginOtherOptions, openAuthloginScreen,
    authLoginScreenClose, errorCallback, paymentPageSuccessCallback: doPaymentPageSuccessCallback,
    validatedDialog,
  };
}
